import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { AppAction } from "tht-impianti-cl";
import { UrlCollection } from "../app-routing.module";
import { PermissionService } from "./permission.service";

/**
 * Dati che una rotta protetta dichiara in `data`.
 *
 * `action` singola o `actions` multiple; con `mode: "all"` servono tutte, altrimenti ne basta una.
 */
export interface PermissionRouteData {
    action?: AppAction;
    actions?: AppAction[];
    mode?: "any" | "all";
}

/**
 * Impedisce di aprire una pagina per cui l'utente non ha il permesso.
 *
 * Prima esistevano solo le guardie di autenticazione, tenant e area: chi digitava `/users` a mano
 * arrivava sulla pagina, che poi restava vuota perché le API rispondevano 403. Il risultato era
 * indistinguibile da un errore.
 *
 * Una rotta senza `data.action` passa: le pagine che non hanno un permesso proprio (la home, il
 * proprio account) non devono dichiarare nulla.
 *
 * Il redirect va a `/403`, che esiste già in `UrlCollection.forbidden`.
 */
export async function PermissionGuardFn(route: ActivatedRouteSnapshot): Promise<boolean> {
    const router = inject(Router);
    const permissions = inject(PermissionService);

    const data = (route.data ?? {}) as PermissionRouteData;
    const actions = data.actions?.length ? data.actions : (data.action ? [data.action] : []);
    if (actions.length === 0) {
        return true;
    }

    const allowed = data.mode === "all"
        ? permissions.canAll(...actions)
        : permissions.canAny(...actions);

    if (allowed) {
        return true;
    }

    await router.navigate([UrlCollection.forbidden]);
    return false;
}
